import { Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import {
  CurrencyPair,
  DepositTakerAndCurrencies,
  Entity,
  LegAttribute,
  MarketDataSpreadingRule,
  MedusaUser,
  ProductGroupings,
  StrategyDetailResponse,
  UserSettings
} from '@approot/models';
import { Header } from '@approot/models/ag-grid.model';
import { clone, Utilities } from '@approot/utilities';
import { forkJoin, Observable, of, throwError } from 'rxjs';
import { catchError, switchMap, take, tap } from 'rxjs/operators';
import { AdminService } from './admin/admin.service';
import { AgGridService } from './ag-grid/ag-grid.service';
import { ConfigService } from './config/config.service';
import { LegService } from './leg/leg.service';
import { PricingExtraService } from './pricing-extra/pricing-extra.service';
import { ProductsConfigService } from './products-config/products-config.service';
import { RfqService } from './rfq/rfq.service';
import { ExecutionContext } from './rfq/rfq.types';
import { StrategyDetailService } from './strategy-detail/strategy-detail.service';
import { TradesCaptureService } from './trade-capture/trades-capture.service';
import { UserStorageService } from './user-storage/user-storage.service';
export interface StartupState {
  state: StartupStates;
  apis: {
    getUserPreferences?: StartupStates;
    rfqCalls?: StartupStates;
    getAvailableDepositTakersAndCurrencies?: StartupStates;
    getUserSettings?: StartupStates;
  };
}

type StartupStates = 'Fetching' | 'Done' | 'Error';

interface StartupConfigCalls {
  userStorage?: Observable<string>;
  rfqCalls?: Observable<ExecutionContext>;
  userSettingsCall?: Observable<UserSettings>;
  depositTakersAndCurrencies?: Observable<DepositTakerAndCurrencies[]>;
}

interface StartupConfig {
  userStorage?: string;
  rfqCalls?: string;
  userSettingsCall?: string;
  depositTakersAndCurrencies?: DepositTakerAndCurrencies[];
}
@Injectable({
  providedIn: 'root'
})
export class StartupApiCallsService {
  public startupState = signal<StartupState>({
    state: null,
    apis: {}
  });

  constructor(
    private productsConfigService: ProductsConfigService,
    private strategyDetailService: StrategyDetailService,
    private rfqService: RfqService,
    private adminService: AdminService,
    private configService: ConfigService,
    private tradesCaptureService: TradesCaptureService,
    private userStorageService: UserStorageService,
    private pricingExtraService: PricingExtraService,
    private legService: LegService,
    private agGridService: AgGridService,
    private router: Router
  ) {}

  public startupCalls(): void {
    this.adminService.setUserInfo();
    const pathname = this.router.url.split('/')[1];
    let calls: StartupConfigCalls = {
      userSettingsCall: this.userSettingsCall,
      userStorage: this.getUserStorage
    };
    this.startupState.update(startState => {
      const clonedState = clone(startState);
      clonedState.state = 'Fetching';
      return clonedState;
    });

    switch (pathname) {
      case 'monitor':
      case 'static-data-management':
        break;
      case '':
        calls = {
          ...calls,
          rfqCalls: this.rfqCalls,
          depositTakersAndCurrencies: this.getDepositTakersAndCurrencies
        };
        break;
      default:
        break;
    }

    if (!Object.keys(calls).length) {
      this.startupStateReady();
      return;
    }

    forkJoin(calls as Record<keyof StartupConfigCalls, Observable<any>>)
      .pipe(
        tap(response => this.startupCallsResponse(response)),
        catchError(err => {
          this.startupState.update(startupState => {
            const clonedState = clone(startupState);
            clonedState.state = 'Error';
            return clonedState;
          });
          return of(err);
        })
      )
      .subscribe();
  }

  private get getDepositTakersAndCurrencies(): Observable<
    DepositTakerAndCurrencies[]
  > {
    this.startupState.update(state => {
      const clonedState = clone(state);
      clonedState.apis.getAvailableDepositTakersAndCurrencies = 'Fetching';
      return clonedState;
    });

    return this.configService.getDepositTakersAndCurrencies.pipe(
      tap(depositTakerCurrencies => {
        this.startupState.update(state => {
          const clonedState = clone(state);
          clonedState.apis.getAvailableDepositTakersAndCurrencies = 'Done';
          return clonedState;
        });
        this.tradesCaptureService.setDepositTakersAndCurrencies(
          depositTakerCurrencies
        );
      }),
      catchError(error => {
        this.startupState.update(state => {
          const clonedState = clone(state);
          clonedState.apis.getAvailableDepositTakersAndCurrencies = 'Error';
          return clonedState;
        });
        return of(error);
      })
    );
  }

  private get rfqCalls(): Observable<ExecutionContext> {
    this.startupState.update(state => {
      const clonedState = clone(state);
      clonedState.apis.rfqCalls = 'Fetching';
      return clonedState;
    });
    let obs: Observable<MedusaUser[]> = this.adminService.medusaCredentials
      ? of(this.adminService.medusaCredentials)
      : this.configService.getMedusaCredentials();
    return obs.pipe(
      switchMap(medusaCredentials => {
        if (medusaCredentials) {
          this.adminService.medusaCredentials = medusaCredentials;

          return this.rfqService
            .getExecutionContext({
              products: [],
              task: 'getExecutionContext'
            })
            .pipe(
              tap(executionContext => {
                this.startupState.update(state => {
                  const clonedState = clone(state);
                  clonedState.apis.rfqCalls = 'Done';
                  return clonedState;
                });
                this.rfqService.executionContext = executionContext;
                const executionType =
                  executionContext.executionTypes[0].shortName;

                this.rfqService.useSpotInRfqPanel =
                  executionContext.useSpotInRfqPanel;

                if (executionType === 'volBased') {
                  this.rfqService.isVolBased = true;
                } else if (
                  executionType === 'premiumBasedVanillasWithSpotFeed'
                ) {
                  this.rfqService.isPremiumBasedVanillasWithSpotFeed = true;
                }
              }),
              catchError(() => {
                this.startupState.update(state => {
                  const clonedState = clone(state);
                  clonedState.apis.rfqCalls = 'Error';
                  return clonedState;
                });
                return throwError('Failed to get execution context');
              })
            );
        }
        this.startupState.update(state => {
          const clonedState = clone(state);
          clonedState.apis.rfqCalls = 'Done';
          return clonedState;
        });
        return of(null);
      }),
      catchError(error => {
        this.startupState.update(state => {
          const clonedState = clone(state);
          clonedState.apis.rfqCalls = 'Error';
          return clonedState;
        });
        return of(error);
      })
    );
  }

  private get getUserStorage(): Observable<string> {
    this.startupState.update(state => {
      const clonedState = clone(state);
      clonedState.apis.getUserPreferences = 'Fetching';
      return clonedState;
    });
    return this.userStorageService.getUserPreferences().pipe(
      tap((storage: string) => {
        this.startupState.update(state => {
          const clonedState = clone(state);
          clonedState.apis.getUserPreferences = 'Done';
          return clonedState;
        });
        if (storage) {
          this.userStorageService.loadStorage(storage);
        } else {
          this.userStorageService.loadFromLocalStorage(
            this.adminService.userInfo
          );
        }
        this.strategyDetailService.setTabsAsReady();
        this.strategyDetailService.localStorageStrategyTabs();
      }),
      catchError(error => {
        this.startupState.update(state => {
          const clonedState = clone(state);
          clonedState.apis.getUserSettings = 'Error';
          return clonedState;
        });
        return of(error);
      })
    );
  }

  private setMarketRuleAndWhiteLabel(
    marketDataSpreadingRule: MarketDataSpreadingRule
  ): void {
    if (marketDataSpreadingRule) {
      this.pricingExtraService.setMarketDataSpreadingRule =
        marketDataSpreadingRule;
    }
  }

  private getDvEntities(): void {
    this.tradesCaptureService.getEntities().subscribe((entities: Entity[]) => {
      this.legService.updateEntities(entities);
      this.tradesCaptureService.allTradeRelatedDataLoaded();
    });
  }

  private setUpTradeGridHeaders(response: StrategyDetailResponse): void {
    const defaultTradeHeaders = response.userSettings.ag_grid_config.find(
      p => p.identifier === 'defaultTradeHeaders'
    ).headers as string[];

    this.tradesCaptureService.setTradeGridHeaders =
      this.agGridService.headers.filter(header =>
        defaultTradeHeaders.includes(header.field)
      );

    if (this.tradesCaptureService.tradesGridHeaders.length) {
      // the trades needs a checkbox on the first item
      this.tradesCaptureService.setTradeGridHeaderIndex(0, {
        ...this.tradesCaptureService.tradesGridHeaders[0],
        headerCheckboxSelection: true,
        checkboxSelection: true,
        headerCheckboxSelectionFilteredOnly: true
      });
    }
  }

  private storeAllCurrencies(currencies: CurrencyPair[]): void {
    // gets all the currencies from the currency pairs and stores them
    let allCurrencies = [];
    currencies.map(currency => {
      const currency1 = currency.currencyPair.substring(0, 3);
      const currency2 = currency.currencyPair.substring(3, 6);
      [currency1, currency2].forEach(currency => {
        if (!allCurrencies.includes(currency)) {
          allCurrencies.push(currency);
        }
      });
    });
    this.pricingExtraService.setCurrencies = allCurrencies;
  }

  private handleUserSettings(userSettings: UserSettings): void {
    const resp: StrategyDetailResponse = {
      userSettings: userSettings
    };

    this.tradesCaptureService.setDvEntities = resp.userSettings.parent;
    this.agGridService.headers = this.agGridService.formatHeaders(
      resp.userSettings.ag_grid_config.find(p => p.identifier === 'headers')
        .headers as Header[]
    ); // format all headers
    this.productsConfigService.productGroupings = resp.userSettings.menu.find(
      p => p.identifier === 'producttypemenu'
    ).items as ProductGroupings[];
    this.productsConfigService.setProductsConfig(resp);
    this.alterProductValidators(userSettings);
    this.storeAllCurrencies(resp.userSettings.currencyPairs || []);
    this.setUpTradeGridHeaders(resp);
    this.getDvEntities();
    this.setMarketRuleAndWhiteLabel(resp.userSettings.marketDataSpreadingRule);

    this.legService.attributesDisplayList =
      this.productsConfigService.productInfo.attributesDisplayList;
    this.legService.productSpecifications =
      this.productsConfigService.productInfo.productSpecifications;
    this.legService.tradeAttributes =
      this.productsConfigService.productInfo.tradeAttributes;
  }

  private alterProductValidators(userSettings: UserSettings): void {
    userSettings.tradeAttributes.attributes.forEach(attribute => {
      this.replaceValidatorWithValidators(attribute);
      this.addExtraValidators(attribute);
    });

    userSettings.products.forEach(product => {
      this.productsConfigService
        .getAllAttributes(product.attributes)
        .forEach(attribute => {
          this.replaceValidatorWithValidators(attribute);
          this.addExtraValidators(attribute);
        });
    });
  }

  private replaceValidatorWithValidators(attribute: LegAttribute): void {
    // can be removed once attribute.validator, is swapped for validators on the backend
    if (!attribute.validators) {
      if (attribute['validator']) {
        attribute.validators = [attribute['validator']];
        delete attribute['validator'];
      } else {
        attribute.validators = [];
      }
    }
  }

  private addExtraValidators(attribute: LegAttribute): void {
    if (
      !attribute.validators.includes('strike') &&
      (Utilities.attributeBelongsToWhichCross(attribute.shortName) ||
        attribute.shortName === 'forwardPointsAsk' ||
        attribute.shortName === 'forwardPointsBid')
    ) {
      attribute.validators.push('formattedDecimal');
    }
  }

  private get userSettingsCall(): Observable<UserSettings> {
    this.startupState.update(state => {
      const clonedState = clone(state);
      clonedState.apis.getUserSettings = 'Fetching';
      return clonedState;
    });
    return this.configService.getUserSettings().pipe(
      take(1),
      tap((userSettings: UserSettings) => {
        this.startupState.update(state => {
          const clonedState = clone(state);
          clonedState.apis.getUserSettings = 'Done';
          return clonedState;
        });
        this.handleUserSettings(userSettings);
      }),
      catchError(error => {
        this.startupState.update(state => {
          const clonedState = clone(state);
          clonedState.apis.getUserSettings = 'Error';
          return clonedState;
        });
        return of(error);
      })
    );
  }

  private startupCallsResponse(response: StartupConfig): void {
    if (response.rfqCalls) {
      this.productsConfigService.handleRfqProducts(
        this.rfqService.executionContext,
        !!this.adminService.medusaCredentials
      );
    }
    this.startupStateReady();
  }

  private startupStateReady(): void {
    this.startupState.update(startupState => {
      const clonedState = clone(startupState);
      clonedState.state = Object.values(clonedState.apis).find(
        state => state !== 'Done'
      )
        ? 'Error'
        : 'Done';
      return clonedState;
    });
  }
}
